/**
 * Export utilities for CSV and Excel downloads
 */

/**
 * Convert data array to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Column definitions [{key, label}]
 * @returns {string} CSV string
 */
export const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return '';

  // Header row
  const headers = columns.map(col => `"${col.label}"`).join(',');

  // Data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.key];

      // Handle null/undefined
      if (value === null || value === undefined) {
        value = '';
      }

      // Format numbers
      if (typeof value === 'number') {
        value = value.toString();
      }

      // Escape quotes and wrap in quotes
      value = String(value).replace(/"/g, '""');
      return `"${value}"`;
    }).join(',');
  });

  return [headers, ...rows].join('\n');
};

/**
 * Download a string as a file
 * @param {string} content - File content
 * @param {string} filename - Name of the file
 * @param {string} mimeType - MIME type
 */
export const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Export data as CSV file
 * @param {Array} data - Array of objects
 * @param {Array} columns - Column definitions [{key, label}]
 * @param {string} filename - Filename without extension
 */
export const exportToCSV = (data, columns, filename) => {
  const csv = convertToCSV(data, columns);
  downloadFile(csv, `${filename}.csv`, 'text/csv;charset=utf-8;');
};

/**
 * Export data as Excel-compatible CSV file (with BOM for proper Excel encoding)
 * @param {Array} data - Array of objects
 * @param {Array} columns - Column definitions [{key, label}]
 * @param {string} filename - Filename without extension
 */
export const exportToExcel = (data, columns, filename) => {
  const csv = convertToCSV(data, columns);
  // Add BOM for Excel to recognize UTF-8
  const bom = '\uFEFF';
  downloadFile(bom + csv, `${filename}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
};

/**
 * Format currency for export
 * @param {number} amount
 * @returns {string}
 */
export const formatCurrencyForExport = (amount) => {
  if (!amount) return '0';
  return Number(amount).toFixed(2);
};

/**
 * Format percentage for export
 * @param {number} value
 * @returns {string}
 */
export const formatPercentageForExport = (value) => {
  if (!value) return '0%';
  return `${Number(value).toFixed(1)}%`;
};

/**
 * Get current date string for filename
 * @returns {string}
 */
export const getDateString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
